import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Text,
  HStack,
  IconButton,
  Select,
} from '@chakra-ui/react';
import { MdAdd, MdDelete } from 'react-icons/md';
import { ValueRefPicker } from './ValueRefPicker';
import type { ValueRefTask } from './valueRefTasks';

interface AutoFillConfigSectionProps {
  entries: { targetRow: string; value: string }[];
  onChange: (entries: { targetRow: string; value: string }[]) => void;
  /** Later tasks in the same list that can be silently auto-filled. */
  laterTasks: { id: string; name: string; index: number; type: string }[];
  /** Tasks the value picker lists, with this task's master data or extracted fields. */
  valueRefTasks: ValueRefTask[];
  /** What triggers the auto-fill, shown in the description — a master_data match for
   * dynamic_choice/text/location_check, or a passing extraction for photo/document/media. */
  triggerDescription?: string;
}

export function AutoFillConfigSection({
  entries,
  onChange,
  laterTasks,
  valueRefTasks,
  triggerDescription = 'When a match is selected here',
}: AutoFillConfigSectionProps) {
  return (
    <Box>
      <VStack align="stretch" spacing={4}>
        <FormLabel color="text-primary" fontSize="md" fontWeight="medium" mb={0}>
          Auto-Fill Configuration
        </FormLabel>
        <Text fontSize="sm" color="text-secondary">
          {triggerDescription}, silently complete later tasks with a value from it instead of
          asking the user again — e.g. the manager field of the store that was picked.
        </Text>

        <Box bg="bg-surface" p={4} rounded="md" borderWidth="1px" borderColor="border-default">
          <FormControl>
            <HStack justify="space-between" mb={2}>
              <FormLabel fontSize="sm" color="text-primary" mb={0}>
                Auto-Filled Tasks
              </FormLabel>
              <IconButton
                aria-label="Add auto-fill target"
                icon={<MdAdd />}
                size="xs"
                colorScheme="brand"
                isDisabled={laterTasks.length === 0}
                onClick={() => onChange([...entries, { targetRow: '', value: '' }])}
                data-testid="wfv4-config-autofill-add-btn"
              />
            </HStack>

            {entries.length > 0 ? (
              <VStack spacing={2} align="stretch">
                {entries.map((entry, index) => (
                  <HStack key={index} spacing={2} align="flex-start">
                    <Select
                      placeholder="Select task"
                      size="sm"
                      value={entry.targetRow}
                      onChange={(e) => {
                        const next = [...entries];
                        next[index] = { ...next[index], targetRow: e.target.value };
                        onChange(next);
                      }}
                      bg="bg-surface"
                      borderColor="border-default"
                      flex={1}
                      data-testid={`wfv4-config-autofill-target-${index}`}
                    >
                      {laterTasks.map((t) => (
                        <option key={t.id} value={t.index}>
                          {t.index}. {t.name}
                        </option>
                      ))}
                    </Select>
                    <Box flex={2} minW={0}>
                      <ValueRefPicker
                        value={entry.value}
                        onChange={(value) => {
                          const next = [...entries];
                          next[index] = { ...next[index], value };
                          onChange(next);
                        }}
                        tasks={valueRefTasks}
                        placeholder="Fill it with…"
                        testId={`wfv4-config-autofill-value-${index}`}
                      />
                    </Box>
                    <IconButton
                      aria-label="Remove auto-fill target"
                      icon={<MdDelete />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => onChange(entries.filter((_, i) => i !== index))}
                      data-testid={`wfv4-config-autofill-remove-${index}`}
                    />
                  </HStack>
                ))}
              </VStack>
            ) : (
              <Text fontSize="xs" color="text-muted" fontStyle="italic">
                {laterTasks.length === 0
                  ? 'Add tasks after this one to configure auto-fill.'
                  : 'No auto-filled tasks configured. Click + to add one.'}
              </Text>
            )}
          </FormControl>
        </Box>
      </VStack>
    </Box>
  );
}
